import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

function money(v: number | undefined): string {
  return (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(part: number | undefined, total: number | undefined): string {
  if (!total) return '—';
  return `${(((part ?? 0) / total) * 100).toFixed(1)}%`;
}

/** Admin · Visão geral — retrato agregado da plataforma (parceiros, lojas, GMV, funil de 30
 *  dias). Consome `GET /api/admin/overview` (`com.storeo.admin`, datasource BYPASSRLS). */
export function VisaoGeral() {
  const overview = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.overview() });
  const d = overview.data;
  const funnel = d?.funnel30d;

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Visão geral</h1>
        <p>Retrato da plataforma hoje — parceiros, lojas, dinheiro e funil dos últimos 30 dias.</p>
      </div>

      <section>
        <div className={ui.secH}>
          <h2>Parceiros</h2>
          <Link to="/parceiros">Ver todos</Link>
        </div>
        <section className={ui.stats} aria-label="Parceiros por status">
          <div className={ui.stat}>
            <span className={ui.statLbl}>Ativos</span>
            <div className={ui.statVal}>{d?.partners.active ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Suspensos</span>
            <div className={ui.statVal}>{d?.partners.suspended ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Em observação</span>
            <div className={ui.statVal}>{d?.partners.watch ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Risco alto</span>
            <div className={ui.statVal}>{d?.partners.high ?? '—'}</div>
          </div>
        </section>
      </section>

      <section>
        <div className={ui.secH}>
          <h2>Lojas</h2>
        </div>
        <section className={ui.stats} aria-label="Lojas por status">
          <div className={ui.stat}>
            <span className={ui.statLbl}>Rascunho</span>
            <div className={ui.statVal}>{d?.stores.draft ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Publicadas</span>
            <div className={ui.statVal}>{d?.stores.published ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Despublicadas</span>
            <div className={ui.statVal}>{d?.stores.unpublished ?? '—'}</div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Suspensas</span>
            <div className={ui.statVal}>{d?.stores.suspended ?? '—'}</div>
          </div>
        </section>
      </section>

      <section>
        <div className={ui.secH}>
          <h2>Dinheiro</h2>
        </div>
        <section className={ui.stats} aria-label="GMV e comissão">
          <div className={ui.stat}>
            <span className={ui.statLbl}>GMV total</span>
            <div className={ui.statVal}>
              <span className={ui.statCur}>R$</span>
              {money(d?.money.gmvAllTime)}
            </div>
            <span className={ui.statSub}>{d?.money.paidOrders ?? 0} pedido(s) pago(s)</span>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>GMV últimos 30d</span>
            <div className={ui.statVal}>
              <span className={ui.statCur}>R$</span>
              {money(d?.money.gmvLast30d)}
            </div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Comissão acumulada</span>
            <div className={ui.statVal}>
              <span className={ui.statCur}>R$</span>
              {money(d?.money.commissionAllTime)}
            </div>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLbl}>Assinaturas ativas</span>
            <div className={ui.statVal}>{d?.activeSubscriptions ?? '—'}</div>
          </div>
        </section>
      </section>

      <section>
        <div className={ui.secH}>
          <h2>Funil (últimos 30 dias)</h2>
        </div>
        {!funnel ? (
          <div className={ui.card}>
            <p className={ui.empty}>Carregando…</p>
          </div>
        ) : (
          <div className={ui.card}>
            <div className={ui.tblScroll}>
              <table className={ui.tbl}>
                <thead>
                  <tr>
                    <th>Etapa</th>
                    <th className="r">Quantidade</th>
                    <th className="r">Conversão</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Visualizações de página</td>
                    <td className="r">
                      <span className={ui.mono}>{funnel.pageViews}</span>
                    </td>
                    <td className="r">—</td>
                  </tr>
                  <tr>
                    <td>Checkouts iniciados</td>
                    <td className="r">
                      <span className={ui.mono}>{funnel.checkoutsStarted}</span>
                    </td>
                    <td className="r">{pct(funnel.checkoutsStarted, funnel.pageViews)}</td>
                  </tr>
                  <tr>
                    <td>Pagos via PIX</td>
                    <td className="r">
                      <span className={ui.mono}>{funnel.paidPix}</span>
                    </td>
                    <td className="r">{pct(funnel.paidPix, funnel.checkoutsStarted)}</td>
                  </tr>
                  <tr>
                    <td>Pagos via cartão</td>
                    <td className="r">
                      <span className={ui.mono}>{funnel.paidCard}</span>
                    </td>
                    <td className="r">{pct(funnel.paidCard, funnel.checkoutsStarted)}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Total pago</b>
                    </td>
                    <td className="r">
                      <span className={ui.money}>
                        R$ {money((funnel.paidAmountCents ?? 0) / 100)}
                      </span>
                    </td>
                    <td className="r">{pct((funnel.paidPix ?? 0) + (funnel.paidCard ?? 0), funnel.checkoutsStarted)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
